import React, {
  createContext, useContext, useState, useEffect, useMemo, useCallback
} from 'react';
import PropTypes from 'prop-types';
import { SWEEPSTAKES_CONSTANTS } from '../core/Constants';
import { windowExists } from '../common/utils/AccountUtils';
import { useSweepstakesData } from './SweepstakesDataContext';

const SweepstakesDrawerContext = createContext();

export const useSweepstakesDrawer = () => {
  const context = useContext(SweepstakesDrawerContext);
  if (!context) {
    throw new Error('useSweepstakesDrawer must be used within a SweepstakesDrawerProvider');
  }
  return context;
};

const defaultDrawerState = {
  isOpen: false,
  drawerType: null,
  selectedStep: null,
  sweepstakesData: null,
};

// Utility functions
const isUserAuthenticated = () => {
  if (typeof window === 'undefined') return true;
  const jwt = window?.THDCustomer?.default?.jwt;
  return jwt && jwt.trim() !== '';
};

const redirectToAuth = () => {
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname + window.location.search;
    const redirectUrl = `/auth/view/signin?redirect=${encodeURIComponent(currentPath)}`;
    window.location.href = redirectUrl;
  }
};

const clearDrawerUrlParams = () => {
  if (windowExists() && window.location.search.includes('drawerList=sweepstakes')) {
    const url = new URL(window.location);
    url.searchParams.delete('drawerList');
    window.history.replaceState({}, '', url.toString());
  }
};

const getDrawerListParam = () => {
  if (!windowExists()) return null;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('drawerList');
};

const createEventHandlers = (closeDrawer, openDrawerWithAuth, handleSurveyCompletion) => {
  const handleOptInDrawer = (eventData) => openDrawerWithAuth(SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN, eventData);
  const handleDetailsDrawer = (eventData) => {
    return openDrawerWithAuth(SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS, eventData);
  };
  const handleSurveyDrawer = (eventData) => openDrawerWithAuth(SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.SURVEY, eventData);
  const handleStepDrawer = (eventData) => openDrawerWithAuth(SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.STEP, eventData);
  const handleNavigate = (eventData, setDrawerState) => setDrawerState((prev) => ({
    ...prev,
    drawerType: eventData.drawerType,
    selectedStep: eventData.selectedStep || null,
  }));

  return {
    'close-sweepstakes-drawer': closeDrawer,
    'open-sweepstakes-opt-in-drawer': handleOptInDrawer,
    'open-sweepstakes-details-drawer': handleDetailsDrawer,
    'open-sweepstakes-survey-drawer': handleSurveyDrawer,
    'open-sweepstakes-step-drawer': handleStepDrawer,
    'navigate-sweepstakes-drawer': handleNavigate,
    'sweepstakes-survey-completed': handleSurveyCompletion
  };
};

const EVENT_TYPES = [
  'open-sweepstakes-opt-in-drawer',
  'open-sweepstakes-details-drawer',
  'open-sweepstakes-survey-drawer',
  'open-sweepstakes-step-drawer',
  'navigate-sweepstakes-drawer',
  'close-sweepstakes-drawer',
  'sweepstakes-survey-completed',
];

export const SweepstakesDrawerProvider = ({ children }) => {
  const [drawerState, setDrawerState] = useState(defaultDrawerState) || {};

  // Access sweepstakes data to handle deep linking when data loads
  const { sweepstakesData, isLoading, updateSweepstakesData } = useSweepstakesData();

  const openDrawer = useCallback((drawerType, options = {}) => {
    if (!isUserAuthenticated()) {
      redirectToAuth();
      return;
    }

    setDrawerState({
      isOpen: true,
      drawerType,
      selectedStep: options.selectedStep || null,
      sweepstakesData: options.sweepstakesData || null,
    });
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerState(defaultDrawerState);
    clearDrawerUrlParams();
  }, []);

  const navigateToDrawer = useCallback((drawerType, options = {}) => {
    setDrawerState((prev) => ({
      ...prev,
      drawerType,
      selectedStep: options.selectedStep || null,
    }));
  }, []);

  const handleStepBack = useCallback(() => {
    navigateToDrawer(SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS);
  }, [navigateToDrawer]);

  const openDrawerWithAuth = useCallback((drawerType, eventData) => {
    if (!isUserAuthenticated()) {
      redirectToAuth();
      return;
    }
    setDrawerState({
      isOpen: true,
      drawerType,
      selectedStep: eventData.selectedStep || null,
      sweepstakesData: eventData.sweepstakesData || null,
    });
  }, []);

  const handleSurveyCompletion = useCallback(() => {
    const newCompletedSteps = (sweepstakesData?.completedSteps || 0) + 1;
    const totalSteps = sweepstakesData?.totalSteps || 4;

    const completionUpdates = {
      ctaTitle: null,
      isOptedIn: true,
      completedSteps: newCompletedSteps,
      status: `${newCompletedSteps} of ${totalSteps} Completed`,
      steps: sweepstakesData?.steps?.map((step) => {
        if (step.id === 'survey') {
          return { ...step, completed: true };
        }
        return step;
      }) || []
    };

    if (updateSweepstakesData) {
      updateSweepstakesData(completionUpdates);
    }

    setDrawerState((prev) => ({
      ...prev,
      drawerType: SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS
    }));
  }, [sweepstakesData, updateSweepstakesData]);

  const eventHandlers = useMemo(() => {
    return createEventHandlers(closeDrawer, openDrawerWithAuth, handleSurveyCompletion);
  }, [closeDrawer, openDrawerWithAuth, handleSurveyCompletion]);

  const handleDrawerEvent = React.useCallback((event, data = {}) => {
    const eventName = typeof event === 'string' ? event : event.eventName;
    const eventData = typeof event === 'string' ? data : (event.output || {});

    const handler = eventHandlers[eventName];
    if (handler) {
      if (eventName === 'navigate-sweepstakes-drawer') {
        handler(eventData, setDrawerState);
      } else {
        handler(eventData);
      }
    }
  }, [eventHandlers]);

  useEffect(() => {
    const drawerListParam = getDrawerListParam();

    if (drawerListParam === 'sweepstakes' && !drawerState.isOpen) {
      if (!isUserAuthenticated()) {
        redirectToAuth();
        return;
      }

      setDrawerState({
        isOpen: true,
        drawerType: SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN,
        selectedStep: null,
        sweepstakesData: null,
      });
    }
  }, [drawerState.isOpen]);

  const handleDeepLink = useCallback((dataForDeepLink) => {
    const drawerListParam = getDrawerListParam();

    if (drawerListParam === 'sweepstakes' && drawerState.isOpen) {
      if (dataForDeepLink?.isOptedIn && drawerState.drawerType === SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN) {
        setDrawerState({
          isOpen: true,
          drawerType: SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS,
          selectedStep: null,
          sweepstakesData: dataForDeepLink,
        });
      } else if (!dataForDeepLink?.isOptedIn) {
        setDrawerState((prev) => ({
          ...prev,
          sweepstakesData: dataForDeepLink,
        }));
      }
    } else if (drawerListParam === 'sweepstakes' && !drawerState.isOpen) {
      const drawerType = dataForDeepLink?.isOptedIn
        ? SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS
        : SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN;

      setDrawerState({
        isOpen: true,
        drawerType,
        selectedStep: null,
        sweepstakesData: dataForDeepLink,
      });
    }
  }, [drawerState.isOpen, drawerState.drawerType]);

  useEffect(() => {
    if (sweepstakesData && !isLoading) {
      handleDeepLink(sweepstakesData);
    }
  }, [sweepstakesData, isLoading, handleDeepLink]);

  useEffect(() => {
    EVENT_TYPES.forEach((eventType) => {
      if (windowExists()) {
        const fullEventName = `loyalty-benefits.${eventType}`;
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on(fullEventName, handleDrawerEvent);
      }
    });

    return () => {
      EVENT_TYPES.forEach((eventType) => {
        if (windowExists()) {
          window.LIFE_CYCLE_EVENT_BUS.lifeCycle.off(`loyalty-benefits.${eventType}`);
        }
      });
    };
  }, [handleDrawerEvent]);

  const contextValue = useMemo(() => ({
    drawerState,
    openDrawer,
    closeDrawer,
    navigateToDrawer,
    handleStepBack,
    DRAWER_TYPES: SWEEPSTAKES_CONSTANTS.DRAWER_TYPES,
  }), [drawerState, openDrawer, closeDrawer, navigateToDrawer, handleStepBack]);

  return (
    <SweepstakesDrawerContext.Provider value={contextValue}>
      {children}
    </SweepstakesDrawerContext.Provider>
  );
};

SweepstakesDrawerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};